import jwtDecode, { type JwtPayload } from "jwt-decode";
import { AttendeeListLinkExpiredResponseFromJSON } from "@simbelapp/order-sdk";
import { FetchResponseTypeEnum, HttpStatusEnum } from "~/utils/enums/common.enums";
import { DocumentLinkKindEnum, DocumentLinkStateEnum } from "~/utils/enums/documents.enums";
import type { IDocumentLink, IDocumentLinkTarget } from "~/utils/interfaces/session-notifications-interfaces";

/** When a document link's JWT expired; null while it has not, or when it cannot be read. */
function tokenExpiredAt(token: string, now: number): string | null {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    return exp && exp * 1000 <= now ? new Date(exp * 1000).toISOString() : null;
  } catch {
    return null;
  }
}

/**
 * Reads the route's answer, fetched without following the redirect: the redirect itself means the
 * link works. A 410 carries the attendee list's expiry; a refused document link has expired once its
 * JWT has, and is unknown otherwise.
 */
export async function documentLinkFromResponse(
  response: Response | null | undefined,
  target: IDocumentLinkTarget,
  downloadUrl: string,
  now = Date.now(),
): Promise<IDocumentLink> {
  const { kind } = target;
  if (response?.type === FetchResponseTypeEnum.OPAQUE_REDIRECT || response?.ok) {
    return { state: DocumentLinkStateEnum.VALID, kind, expiredAt: null, downloadUrl };
  }

  if (response?.status === HttpStatusEnum.GONE) {
    const body = await response.json().catch(() => null);
    const expiredAt = body ? (AttendeeListLinkExpiredResponseFromJSON(body).expiredAt ?? null) : null;
    return { state: DocumentLinkStateEnum.EXPIRED, kind, expiredAt, downloadUrl: null };
  }

  const expiredAt = kind === DocumentLinkKindEnum.DOCUMENT ? tokenExpiredAt(target.credential, now) : null;
  return expiredAt
    ? { state: DocumentLinkStateEnum.EXPIRED, kind, expiredAt, downloadUrl: null }
    : { state: DocumentLinkStateEnum.NOT_FOUND, kind, expiredAt: null, downloadUrl: null };
}
